import { Plus, ArrowRight, ArrowLeft } from "@phosphor-icons/react/dist/ssr";
import { ArrayFieldTemplateProps } from "@rjsf/utils";
import { t } from "i18next";
import useUpdateSectionAction from "../../actions/updateSection/useUpdateSectionAction";
import { Button } from "react-bootstrap";
import { useState } from "react";

function GalleryArrayTemplate(
  props: ArrayFieldTemplateProps,
  className: string,
  id: string
) {
  const { actionTemplate } = useUpdateSectionAction();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Añadir clase visual al elemento arrastrado
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "0.5";
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "1";
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      const element = props.items[draggedIndex];
      if (element && element.onReorderClick) {
        element.onReorderClick(draggedIndex, targetIndex)();
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className={'cms-section '+className}>
      {id !== "root_sections" ? <h2 className="cms-section-title">{props.title} {actionTemplate}</h2> : null}
        <div className="gallery-grid">
          {props.items.map((element, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;
            return (
                <div
                  className={`gallery-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                  key={element.key}
                  draggable={id !== "root_sections"}
                  onDragStart={handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver(index)}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(index)}
                >
                  {id !== "root_sections" ? (
                    <div className="gallery-item-header">
                      <h3 className="gallery-item-title sr-only">
                        {t("image")} {index + 1}{" "}
                      </h3>
                      {props.canAdd && (
                        <Button className="gallery-item-remove" variant="light" onClick={element.onDropIndexClick(index)}>
                          <span className="sr-only">{t("delete")}</span>
                        </Button>
                      )}
                    </div>
                  ) : null}

                  <div className="flex-grow-1">{element.children}</div>

                  {/* Botones de ordenación (mantener como alternativa de accesibilidad) */}
                  <div className="gallery-item-sort">
                    {element.hasMoveUp && (
                      <Button
                        variant="light"
                        className="btn-icon"
                        onClick={element.onReorderClick(index, index - 1)}
                        title={t("move_up")}
                      >
                        <ArrowLeft size={16} />
                        <span className="sr-only">{t("move_up")}</span>
                      </Button>
                    )}
                    {element.hasMoveDown && (
                      <Button
                        variant="light"
                        className="btn-icon"
                        onClick={element.onReorderClick(index, index + 1)}
                        title={t("move_down")}
                      >
                        <ArrowRight size={16} />
                        <span className="sr-only">{t("move_down")}</span>
                      </Button>
                    )}
                  </div>
                </div>
            );
          })}
        </div>

        <div className="text-center mt-4">
          {props.canAdd && (
            <Button
              variant="outline-primary"
              onClick={props.onAddClick}
            >
              <Plus size={24} />
              <span>{t("add_image")}</span>
            </Button>
          )}
        </div>
    </div>
  );
}

export default GalleryArrayTemplate;
